import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Icon, Input } from 'semantic-ui-react';
import { findDOMNode } from 'react-dom';

import ReactTable from 'Common/ReactTableWrapper';
import { ItemEnum } from 'Common/FieldEnum';
import { ToolTipComponent } from 'Components/toolTipComponent/ToolTipComponent';
import EditableLabel from 'Components/editableLabel/EditableLabel';
import { BracketTypeEnum } from 'Common/Constants/Enumeration/ManageProcessOrderEnum';
import { SecurityPreferences } from 'Common/Security/SecurityPreferences';
import { createKeyEventListener } from 'Common/GeneralHelper';
import {
  caseInsensitiveFilter,
  numericSortMethod,
  qtyOnHandSortMethod,
  dateSortMethod,
} from 'Common/GridHelper';
import {
  ReactTableWrapperColumnProperties,
  PACK_SIZE_MESSAGE,
} from 'Common/Constants';
import {
  dollarCalculation,
  quantityCalculation,
  weightCalculation,
} from 'Common/FormHelper';
import { PreferencesEnum } from 'Common/Security/PreferencesEnum';

class ItemGridContainer extends Component {
  static propTypes = {
    tableName: PropTypes.string.isRequired,
    negativeQtyAllowed: PropTypes.bool,
    searchColumns: PropTypes.array,
    orderItems: PropTypes.object,
    searchItems: PropTypes.array,
    onSearchSubmit: PropTypes.func,
    onQuantityChange: PropTypes.func.isRequired,
    onItemRemove: PropTypes.func,
    disabled: PropTypes.bool,
    showRemoveButton: PropTypes.bool,
    showCalc: PropTypes.bool,
    priceSecurityScope: PropTypes.oneOf(Object.values(PreferencesEnum)),
  };

  static defaultProps = {
    negativeQtyAllowed: false,
    searchColumns: [],
    searchItems: [],
    orderItems: [],
    onSearchSubmit: () => {},
    onItemRemove: () => {},
    disabled: false,
    showRemoveButton: true,
    showCalc: false,
  };

  state = {
    search: {},
    searchResult: null,
    itemToFocus: null,
  };

  componentDidUpdate() {
    const { itemToFocus } = this.state;

    //if we just added an item from our search, or if we're typing in an input
    //that may change row orders, focus on an item's input
    if (itemToFocus) {
      const { field, itemCode, caretPos } = itemToFocus;

      const node = findDOMNode(this);

      let className;

      if (field === ItemEnum.PriQty) {
        className = 'purchaseqty';
      } else if (field === ItemEnum.SecQty) {
        className = 'packqty';
      } else if (field === ItemEnum.PrimaryPrice) {
        className = 'primaryprice';
      }

      if (className) {
        const input = node.querySelector(
          `.itemgrid-input-${className}[data-itemcode="${itemCode}"] > input`
        );

        if (input) {
          input.focus();

          if (caretPos !== undefined) {
            input.setSelectionRange(caretPos, caretPos);
          }
        }
      }
    }
  }

  createFooters = (column) => {
    const newColumn = { ...column };

    newColumn.Footer = () => {
      const { searchColumns, searchItems } = this.props;
      const { searchResult } = this.state;

      //if this is a searchable column, show input
      if (searchColumns.includes(column.accessor)) {
        return (
          <Input
            type="text"
            className="itemgrid-search-input"
            disabled={!searchItems.length}
            placeholder={
              searchResult
                ? searchResult[column.accessor]
                : `Enter ${column.visibilityName || column.Header}`
            }
            onChange={(e) =>
              this.handleSearchChange(column.accessor, e.target.value)
            }
            onKeyDown={createKeyEventListener({
              onTab: (e) => this.handleSearchTab(column.accessor, e.target),
              onEnter: (e) => this.handleSearchEnter(column.accessor, e.target),
            })}
          />
        );
      }

      //otherwise, show a preview of the current search result
      if (searchResult) {
        if (column.Cell) {
          return column.Cell({ ...column, original: searchResult });
        }

        return searchResult[column.accessor] || '';
      }

      return null;
    };

    newColumn.footerStyle = { opacity: 0.75 };

    return newColumn;
  };

  columns = (() => {
    let columns = [
      {
        Header: 'Item Code',
        accessor: ItemEnum.ItemCode,
        ...ReactTableWrapperColumnProperties,
        width: 160,
        locked: true,
      },
      {
        Header: 'Description',
        accessor: ItemEnum.Description,
        ...ReactTableWrapperColumnProperties,
        width: 200,
        locked: true,
      },
      {
        Header: 'Kitchen Item',
        accessor: ItemEnum.PrepareInHouse,
        ...ReactTableWrapperColumnProperties,
      },
      {
        Header: () => {
          return (
            <ToolTipComponent
              label="Pack Size"
              popMessage={PACK_SIZE_MESSAGE}
              iconName="question circle outline"
            />
          );
        },
        visibilityName: 'Pack Size',
        accessor: ItemEnum.QtyPerUnit,
        ...ReactTableWrapperColumnProperties,
      },
      {
        Header: 'Item Packaging',
        accessor: ItemEnum.ItemPackaging,
        ...ReactTableWrapperColumnProperties,
      },
      {
        Header: 'Purchase Qty',
        accessor: ItemEnum.PriQty,
        className: 'right-align',
        ...ReactTableWrapperColumnProperties,
        visibility: false,
        Cell: ({ original }) => {
          const { orderItems, disabled } = this.props;

          const itemCode = original[ItemEnum.ItemCode];
          const orderItem = orderItems[itemCode] || original;

          const self = this;

          return (
            <EditableLabel
              label={orderItem[ItemEnum.PriQty] || ''}
              labelClassName="qtyLabel"
              className="itemgrid-input itemgrid-input-purchaseqty"
              data-itemcode={itemCode}
              inputType="text"
              allowEdit={true}
              onChange={function () {
                const target = findDOMNode(this).querySelector('input');
                self.handleInputChange(original, ItemEnum.PriQty, target);
              }}
              onKeyDown={createKeyEventListener({
                onTab: (e) => this.handleInputTab(ItemEnum.PriQty, e.target),
                onEnter: (e) =>
                  this.handleInputEnter(ItemEnum.PriQty, e.target),
              })}
              onBlur={this.handleInputBlur}
              disabled={disabled}
            />
          );
        },
        sortMethod: numericSortMethod,
      },
      {
        Header: 'Unit',
        accessor: ItemEnum.Unit,
        ...ReactTableWrapperColumnProperties,
      },
      {
        Header: 'Pack Qty',
        accessor: ItemEnum.SecQty,
        className: 'right-align',
        ...ReactTableWrapperColumnProperties,
        visibility: false,
        Cell: ({ original }) => {
          if (!original.AllowPartialOrdering) {
            return <div style={{ height: 20 }} />;
          }

          const { orderItems, disabled, showCalc } = this.props;

          const itemCode = original[ItemEnum.ItemCode];
          const orderItem = orderItems[itemCode] || original;

          const self = this;

          return (
            <EditableLabel
              label={orderItem[ItemEnum.SecQty] || ''}
              labelClassName="qtyLabel"
              className="itemgrid-input itemgrid-input-packqty"
              data-itemcode={itemCode}
              inputType="text"
              allowEdit={true}
              onChange={function () {
                const target = findDOMNode(this).querySelector('input');
                self.handleInputChange(original, ItemEnum.SecQty, target);
              }}
              onKeyDown={createKeyEventListener({
                onTab: (e) => this.handleInputTab(ItemEnum.SecQty, e.target),
                onEnter: (e) =>
                  this.handleInputEnter(ItemEnum.SecQty, e.target),
              })}
              onBlur={this.handleInputBlur}
              disabled={disabled}
              showCalc={showCalc}
            />
          );
        },
        sortMethod: numericSortMethod,
      },
      {
        Header: 'Pack Unit',
        accessor: ItemEnum.SecondaryUnit,
        ...ReactTableWrapperColumnProperties,
      },
      {
        Header: 'Price',
        accessor: ItemEnum.PrimaryPrice,
        className: 'right-align',
        Cell: ({ original }) => {
          const { priceSecurityScope, orderItems, disabled } = this.props;

          const itemCode = original[ItemEnum.ItemCode];
          const orderItem = orderItems[itemCode] || original;

          //display based on bracket pricing
          let price = orderItem[ItemEnum.PrimaryPrice];

          if (
            orderItem.BracketPricingDetails &&
            orderItem.BracketPricingDetails.length > 0
          ) {
            if (orderItem.BracketType === BracketTypeEnum.Dollars) {
              price = dollarCalculation(
                orderItem,
                +orderItem.BracketPricingDetails[0].Price,
                true
              );
            } else if (orderItem.BracketType === BracketTypeEnum.Quantity) {
              price = quantityCalculation(orderItem, true);
            } else if (orderItem.BracketType === BracketTypeEnum.Weight) {
              price = weightCalculation(orderItem, true);
            }
          }

          //if the security preference is enabled, is possible to edit pricing for
          //items while adding them to an order
          const self = this;

          if (priceSecurityScope) {
            const {
              [ItemEnum.PriQty]: priQty,
              [ItemEnum.SecQty]: secQty,
            } = orderItem;

            return (
              <SecurityPreferences scope={priceSecurityScope}>
                {(preferenceDisabled) => {
                  if (preferenceDisabled || (!priQty && !secQty)) {
                    return <span>{Number(price).toFixed(4)}</span>;
                  }

                  return (
                    <EditableLabel
                      label={price ? Number(price).toFixed(4) : ''}
                      labelClassName="qtyLabel"
                      className="itemgrid-input itemgrid-input-primaryprice"
                      data-itemcode={itemCode}
                      allowEdit={true}
                      onChange={function () {
                        const target = findDOMNode(this).querySelector('input');

                        self.handleInputChange(
                          original,
                          ItemEnum.PrimaryPrice,
                          target
                        );
                      }}
                      onKeyDown={createKeyEventListener({
                        onTab: (e) =>
                          this.handleInputTab(ItemEnum.PrimaryPrice, e.target),
                        onEnter: (e) =>
                          this.handleInputEnter(
                            ItemEnum.PrimaryPrice,
                            e.target
                          ),
                      })}
                      onBlur={this.handleInputBlur}
                      disabled={disabled}
                    />
                  );
                }}
              </SecurityPreferences>
            );
          }

          return <span>{Number(price).toFixed(4)}</span>;
        },
        ...ReactTableWrapperColumnProperties,
        sortable: !this.props.scope,
        sortMethod: numericSortMethod,
      },
      {
        Header: 'Distributor',
        accessor: ItemEnum.VendorName,
        ...ReactTableWrapperColumnProperties,
      },
      {
        Header: 'Qty On Hand',
        accessor: ItemEnum.QtyOnHand_Detail,
        sortMethod: qtyOnHandSortMethod,
        ...ReactTableWrapperColumnProperties,
      },
      {
        Header: 'Alternate Item',
        accessor: ItemEnum.AlternateItemId,
        ...ReactTableWrapperColumnProperties,
        show: false,
      },
      {
        Header: 'Account Category',
        accessor: ItemEnum.AccountCategory,
        ...ReactTableWrapperColumnProperties,
        show: false,
      },
      {
        Header: 'Bid Group',
        accessor: ItemEnum.BidGroup,
        ...ReactTableWrapperColumnProperties,
        show: false,
      },
      {
        Header: 'Tax Group',
        accessor: ItemEnum.TaxGroup,
        ...ReactTableWrapperColumnProperties,
        show: false,
      },
      {
        Header: 'Distributor Item',
        accessor: ItemEnum.VendorItem,
        ...ReactTableWrapperColumnProperties,
        show: false,
      },
      {
        Header: 'Brand',
        accessor: ItemEnum.CurrentBrand,
        ...ReactTableWrapperColumnProperties,
        show: false,
      },
      {
        Header: 'UPC',
        accessor: ItemEnum.UPC,
        ...ReactTableWrapperColumnProperties,
        show: false,
      },
      {
        Header: 'GTIN',
        accessor: ItemEnum.GTINNumber,
        className: 'right-align',
        ...ReactTableWrapperColumnProperties,
        show: false,
      },
      {
        Header: 'Begin Date',
        accessor: ItemEnum.BeginDate,
        ...ReactTableWrapperColumnProperties,
        show: false,
        sortMethod: dateSortMethod,
      },
      {
        Header: 'End Date',
        accessor: ItemEnum.EndDate,
        ...ReactTableWrapperColumnProperties,
        show: false,
        sortMethod: dateSortMethod,
      },
      {
        Header: 'Servings Per Purchase Unit',
        accessor: ItemEnum.ServingsPerPrimaryUnit,
        className: 'right-align',
        ...ReactTableWrapperColumnProperties,
        show: false,
      },
      {
        Header: 'Menu Costing',
        accessor: ItemEnum.MenuCosting,
        ...ReactTableWrapperColumnProperties,
        show: false,
      },
      {
        Header: 'Order Preference',
        accessor: ItemEnum.OrderPreference,
        ...ReactTableWrapperColumnProperties,
        show: false,
      },
      {
        Header: 'Storage Location',
        accessor: ItemEnum.StorageLocation,
        ...ReactTableWrapperColumnProperties,
        show: false,
      },
      {
        Header: 'Minimum Par Level',
        accessor: ItemEnum.MinimumParLevel,
        className: 'right-align',
        ...ReactTableWrapperColumnProperties,
        show: false,
      },
      {
        Header: 'Maximum Par Level',
        accessor: ItemEnum.MaximumParLevel,
        className: 'right-align',
        ...ReactTableWrapperColumnProperties,
        show: false,
      },
      {
        Header: 'Truck Route',
        accessor: ItemEnum.TruckRoute,
        ...ReactTableWrapperColumnProperties,
        show: false,
      },
      {
        resizable: false,
        width: 50,
        sortable: false,
        filterable: false,
        Cell: ({ original }) => {
          const {
            showRemoveButton,
            disabled,
            orderItems,
            onItemRemove,
          } = this.props;

          const itemCode = original[ItemEnum.ItemCode];

          if (!showRemoveButton || disabled || !orderItems[itemCode]) {
            return null;
          }

          return (
            <Icon
              name="minus circle"
              style={{ cursor: 'pointer' }}
              onClick={() => onItemRemove(original)}
            />
          );
        },
      },
    ];

    //only add footers if at least one search column is found
    const { searchColumns } = this.props;

    let searchColumnFound = false;

    for (let i = 0; i < searchColumns.length; i++) {
      for (let j = 0; j < columns.length; j++) {
        if (columns[j].accessor === searchColumns[i]) {
          searchColumnFound = true;
          break;
        }
      }
    }

    if (searchColumnFound) {
      columns = columns.map(this.createFooters);
    }

    return columns;
  })();

  handleInputChange = (item, accessor, target) => {
    const { orderItems, negativeQtyAllowed, onQuantityChange } = this.props;
    const rawValue = target.value;

    //normalize input
    const pattern = '\\d{0,6}(\\.\\d{0,4})?';
    const regexp = new RegExp(`${negativeQtyAllowed ? '^-?' : ''}${pattern}`);
    const value = (rawValue.match(regexp)[0] || '').trim();

    //if the value is invalid, exit
    if (rawValue.trim() && !value) {
      return;
    }

    //our row order may change, so save where our cursor is
    const itemCode = item[ItemEnum.ItemCode];
    const caretPos = target.selectionStart;

    this.setState({ itemToFocus: { field: accessor, itemCode, caretPos } });

    //clone the item and save the new version
    const oldOrderItem = orderItems[itemCode] || item;
    const orderItem = { ...oldOrderItem, [accessor]: value };

    onQuantityChange(orderItem);
  };

  handleInputTab = (accessor, target) => {
    //if this item has another input, focus it
    //otherwise treat this tab like it was an enter
    const node = findDOMNode(this);
    const itemCode = target.parentNode.dataset.itemcode;
    const allInputs = [
      ...node.querySelectorAll(`.itemgrid-input[data-itemcode="${itemCode}"]`),
    ];
    const inputIndex = allInputs.indexOf(target.parentNode);
    const nextParent = inputIndex > -1 ? allInputs[inputIndex + 1] : null;
    const nextInput = nextParent ? nextParent.querySelector('input') : null;

    if (nextInput) {
      nextInput.focus();
      return true;
    }

    return this.handleInputEnter(accessor, target);
  };

  handleInputEnter = (accessor, target) => {
    //if this grid has a search input, focus it
    //otherwise, move to next row
    const node = findDOMNode(this);
    const searchInput = node.querySelector('.itemgrid-search-input > input');

    if (searchInput) {
      searchInput.focus();
    } else {
      const { data } = this.props;

      const curItemCode = target.parentNode.dataset.itemcode;
      const curIndex = data.findIndex(
        (item) => item[ItemEnum.ItemCode] === curItemCode
      );
      const nextItem = data[curIndex + 1];

      if (nextItem) {
        const nextInput = node.querySelector(
          `.itemgrid-input[data-itemcode="${
            nextItem[ItemEnum.ItemCode]
          }"] > input`
        );

        if (nextInput) {
          nextInput.focus();
        }
      }
    }

    return true;
  };

  handleInputBlur = () => {
    this.setState({ itemToFocus: null });
  };

  handleSearchChange = (accessor, filter) => {
    const oldSearch = this.state.search;
    const search = { ...oldSearch };

    if (filter) {
      search[accessor] = filter;
    } else {
      delete search[accessor];
    }

    this.setState({ search }, this.updateSearchResult);
  };

  updateSearchResult = () => {
    const { searchItems, orderItems } = this.props;
    const { search, searchResult: oldSearchResult } = this.state;

    const searchFilters = Object.entries(search);

    if (searchFilters.length) {
      const searchResult = searchItems.find((item) => {
        //don't allow searching for items that are already in our order
        if (orderItems[item[ItemEnum.ItemCode]]) {
          return false;
        }

        //make sure the item matches all filters
        for (let i = 0; i < searchFilters.length; i++) {
          const [id, value] = searchFilters[i];

          if (!caseInsensitiveFilter({ id, value }, item)) {
            return false;
          }
        }

        return true;
      });

      if (searchResult) {
        if (searchResult !== oldSearchResult) {
          this.setState({ searchResult });
        }

        return;
      }
    }

    if (oldSearchResult) {
      this.setState({ searchResult: null });
    }
  };

  handleSearchTab = (column, target) => {
    if (column === ItemEnum.Description) {
      this.handleSearchEnter(column, target);
      return true;
    }

    return false;
  };

  handleSearchEnter = (column, target) => {
    const { onSearchSubmit } = this.props;
    const { searchResult } = this.state;

    if (searchResult) {
      onSearchSubmit(searchResult);

      this.setState({
        searchResult: null,
        itemToFocus: {
          field: ItemEnum.PriQty,
          itemCode: searchResult[ItemEnum.ItemCode],
        },
      });
    }

    //clear all search inputs
    const node = findDOMNode(this);
    const inputs = node.querySelectorAll('.itemgrid-search-input > input');

    for (let i = 0; i < inputs.length; i++) {
      inputs[i].value = '';
    }

    return true;
  };

  render() {
    const {
      gridPreferenceLoading,
      gridPreferenceSubmitting,
      loading,
      tableName,
      ...tableProps
    } = this.props;

    return (
      <ReactTable
        minRows={0}
        loading={loading || gridPreferenceLoading || gridPreferenceSubmitting}
        resizable={true}
        defaultFilterMethod={caseInsensitiveFilter}
        draggableColumns={true}
        showHideColumn={true}
        className="-striped -highlight rt-filterfix"
        menu="hasCogs"
        columns={this.columns}
        saveState={{ tableName, filtered: false }}
        resetPageOnDataChange={false}
        getTheadThProps={() => ({ style: { whiteSpace: 'normal' } })}
        showFiltersPlaceholderText
        {...tableProps}
      />
    );
  }
}

const mapStateToProps = (state) => {
  const { gridPreference } = state.CommonState;

  return {
    gridPreferenceLoading: gridPreference.loading,
    gridPreferenceSubmitting: gridPreference.isSubmitting,
  };
};

export const ItemGrid = connect(mapStateToProps, null)(ItemGridContainer);



// WEBPACK FOOTER //
// ./src/components/itemgridformfield/ItemGrid.js